import React, { useState, useEffect } from "react";
import { Table, Button, Modal, Input, Space, Card, Typography } from "antd";
import { DownloadOutlined, EyeOutlined, SearchOutlined } from "@ant-design/icons";
import axios from "axios";
import Swal from "sweetalert2"; // Import SweetAlert
import { BASE_URL } from "../../App";
import dayjs from "dayjs";
const { Title, Text } = Typography;

const DocumentsTable = () => {
  const [data, setData] = useState([]);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewFile, setPreviewFile] = useState("");
  const [docNo, setDocNo] = useState("");
  const [loading, setLoading] = useState(false);

  //const login_id = "MKT00117"; // Replace with localStorage.getItem("LOGIN_ID") if needed
  const login_id = localStorage.getItem("LOGIN_ID");
  const usertype = localStorage.getItem("usertype");
  useEffect(() => {
    fetchDocuments();
  }, []);

  const fetchDocuments = async () => {
    setLoading(true);
    try {
      const response = await axios.post(BASE_URL + "/ddpdocuments", { login_id,usertype });
      setData(response.data.documents || []);
    } catch (error) {
      console.error("Error fetching documents:", error);
    }
    setLoading(false);
  };

  const fetchDocumentsByDocNo = async () => {
    if (!docNo.trim()) {
      Swal.fire({
        icon: "warning",
        title: "Invalid Search",
        text: "Please enter a valid DOC NO before searching.",
      });
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(BASE_URL + "/ddpdocumentswithdocno", { login_id, doc_no: docNo });
      const documents = response.data.documents || [];

      if (documents.length === 0) {
        Swal.fire({
          icon: "info",
          title: "No Documents Found",
          text: `No documents available for DOC NO: ${docNo}.`,
        });
      }

      setData(documents);
    } catch (error) {
      console.error("Error fetching document:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Something went wrong while fetching documents.",
      });
    }
    setLoading(false);
  };

  const isBase64 = (str) => {
    return str.startsWith("data:image/");
  };

  const handleDownload = (fileUrl, docNo) => {
    if (!fileUrl) {
      Swal.fire({
        icon: "warning",
        title: "Download Failed",
        text: "No file available for download.",
      });
      return;
    }

    if (isBase64(fileUrl)) {
      const mimeType = fileUrl.substring(fileUrl.indexOf(":") + 1, fileUrl.indexOf(";"));
      const ext = mimeType.split("/")[1];
      const base64Data = fileUrl.split(",")[1];

      const byteCharacters = atob(base64Data);
      const byteNumbers = Array.from(byteCharacters, (char) => char.charCodeAt(0));
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: mimeType });
      const url = URL.createObjectURL(blob);

      const a = document.createElement("a");
      a.href = url;
      a.download = `Document_${docNo}.${ext}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } else {
      const a = document.createElement("a");
      a.href = fileUrl;
      a.download = `Document_${docNo}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    }
  };

  const handlePreview = (fileUrl) => {
    setPreviewFile(fileUrl);
    setPreviewVisible(true);
  };

  const columns = [
    { title: "DOC NO", dataIndex: "DOC_NO", key: "doc_no", width: 120 },
    { title: "ACTIVITY OBJECTIVE", dataIndex: "DESCRIPTION", key: "description" },
    { title: "DEALER", dataIndex: "DEALER_CODE", key: "dealer_code", width: 120 },
    { title: "BUDGET", dataIndex: "BUDGET", key: "BUDGET", width: 120 },
    { title: "APPROVED BUDGET", dataIndex: "APPROVED_BUDGET", key: "APPROVED_BUDGET", width: 120 },
    { title: "ACTIVITY TYPE", dataIndex: "ACTIVITY_TYPE", key: "activity_type", width: 150 },
    { 
        title: "FROM DATE", 
        dataIndex: "FROM_DATE", 
        key: "FROM_DATE", 
        width: 150,
        render: (text) => text ? dayjs(text).format("DD-MMM-YYYY") : "N/A"
      }, 
      { 
        title: "TO DATE", 
        dataIndex: "TO_DATE", 
        key: "TO_DATE", 
        width: 150,
        render: (text) => text ? dayjs(text).format("DD-MMM-YYYY") : "N/A"
      },
    {
      title: "File Preview",
      dataIndex: "filepath",
      key: "filepath",
      render: (fileUrl) => {
        if (!fileUrl) return "No File";
        if (isBase64(fileUrl)) {
          return (
            <img
              src={fileUrl}
              alt="Preview"
              style={{ width: 50, height: 50, cursor: "pointer", borderRadius: 5 }}
              onClick={() => handlePreview(fileUrl)}
            />
          );
        } else if (fileUrl.endsWith(".pdf")) {
          return (
            <Button type="link" icon={<EyeOutlined />} onClick={() => handlePreview(fileUrl)}>
              View PDF
            </Button>
          );
        }
        return <a href={fileUrl} target="_blank" rel="noopener noreferrer">Download File</a>;
      },
    },
    {
      title: "Download",
      key: "download",
      render: (_, record) => (
        <Button
          type="primary"
          icon={<DownloadOutlined />}
          onClick={() => handleDownload(record.filepath, record.DOC_NO)}
        >
          Download
        </Button>
      ),
    },
  ];

  return (
    <div style={{ padding: 24, background: "#f5f5f5", minHeight: "100vh" }}>
      <Card style={{ marginBottom: 24, padding: 16, borderRadius: 8 }}>
        <Title level={4} style={{ marginBottom: 16 }}></Title>
        <Space size="large">
          <div>
            <Text strong>DOC NO:</Text>
            <Input
              placeholder="Enter DOC NO"
              value={docNo}
              onChange={(e) => setDocNo(e.target.value)}
              style={{ width: 250, marginLeft: 8 }}
            />
          </div>
          <Button type="primary" icon={<SearchOutlined />} onClick={fetchDocumentsByDocNo} loading={loading}>
            Search
          </Button>
        </Space>
      </Card>

      <Card style={{ borderRadius: 8 }}>
        <Table 
          dataSource={data} 
          columns={columns} 
          rowKey="DOC_NO" 
          loading={loading} 
          pagination={{ pageSize: 10 }} 
          bordered
        />
      </Card>

      {/* Preview Modal */}
      <Modal 
        open={previewVisible} 
        footer={null} 
        onCancel={() => setPreviewVisible(false)} 
        width={800}
        style={{ top: 20 }}
      >
        {previewFile.startsWith("data:image") ? (
          <img alt="Preview" style={{ width: "100%", borderRadius: 8 }} src={previewFile} />
        ) : (
          <iframe src={previewFile} title="Preview" width="100%" height="600px" />
        )}
      </Modal>
    </div>
  );
};

export default DocumentsTable;
